import { Component, ViewChild, ElementRef } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import {
  AuthService, BreadcrumbService, CompanyService, ConfirmDialogService, FacadeDownloadService, FacadeHomesService, HighlightService, HomeService, StateService
} from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CreateSituFacadeRequest, ProductName, SituViewWebApiClient, GetSituFacadeRequest, 
  GetSituFacadeResponse, RenderedOutputStatus, SetRenderedOutputProductionRequest, SetRenderedOutputProductionResponse, 
  GetSituFacadeWithIdsRequest, HomeConfigSlim, GetDefaultRenderedOutputRequest, GetDefaultRenderedOutputResponse, SetDefaultRenderedOutputRequest, 
  SetDefaultRenderedOutputResponse, CreateSituFacadeChildRequest,
  ListIfcFilesByHomeIdRequest, GetHomeConfigRequest, GetHomeConfigSlimIdRequest,
  GetHomeConfigSlimIdResponse, GetHomeConfigResponse, HomeIfcFile, HomeIfcFileType, ListReleaseItemsRequest, ListReleaseItemsResponse,
  HomeConfig, HomeConfigFile, EnvironmentName, ArtifactEnvironment
} from 'situview-webapi-client';
import {
  SituRenderServiceWebApiClient,
  AddPriorityConfigResponse, PriorityConfig, AddPriorityConfigRequest
} from 'siturenderservice-webapi-client';
import { SituAnalyticsWebApiClient, GetRendererNamesRequest } from 'situanalytics-webapi-client';
import { ConfigService } from 'src/app/services/config.service';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import 'prismjs';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-regex';
import { ActivatedRoute, Router } from '@angular/router';
declare var Prism: any;
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { Guid } from 'guid-typescript';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';
import { Role } from 'src/app/shared/models/role.model';
import { ReleaseStyleTemplateDialogComponent, ReleaseStyleTemplateDialogComponentResultData } from '../../admin/product-releases/release-style-template-dialog/release-style-template-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ReleaseIssueDialogComponent, ReleaseIssueDialogComponentResultData } from '../../admin/product-releases/release-issue-dialog/release-issue-dialog.component';
import { ScratchTemplateDialogComponent, IScratchTemplateDialogData } from '../scratch-template-dialog/scratch-template-dialog.component';
import { isDaydreamProduct } from 'src/app/shared/models/daydream-product';

@Component({
  selector: 'situ-facade',
  templateUrl: './situ-facade.component.html',
  styleUrls: ['./situ-facade.component.scss']
})
export class SituFacadeComponent extends SituBaseComponent {
  /* view high level controls */
  collapsed: boolean = false;
  paramId: string | undefined;
  paramToId: string | undefined;
  initialised: boolean = false;
  readonly facadeProductName: ProductName = ProductName.Facade;
  isReleasePermitted: boolean = false;
  advancedMode: boolean = false;

  // Advanced mode: pass a specific renderer (queue) and environment through to the render request
  renderers: string[] = [];
  selectedRenderer: string | undefined;
  selectedEnvironment: EnvironmentName | undefined;
  environmentOptions: { label: string; value: EnvironmentName }[] = [
    { label: 'Latest', value: EnvironmentName.Latest },
    { label: 'Staging', value: EnvironmentName.Staging },
    { label: 'Production', value: EnvironmentName.Production },
    { label: 'Fallback', value: EnvironmentName.Fallback },
  ];

  toggleAdvancedMode(): void {
    this.advancedMode = !this.advancedMode;
    if (this.advancedMode && this.renderers.length === 0) {
      this.loadRenderers();
    }
  }

  // Open the scratch pad to move themes in/out of this user's scratch template, then render it.
  openScratchPad(): void {
    const dialogRef = this.dialog.open(ScratchTemplateDialogComponent, {
      width: '90vw',
      maxWidth: '1400px',
      data: {
        companyId: this.selectedCompany?.id,
        baseTemplateId: this.selectedStyleTemplateId,
        baseTemplateName: this.renderedOutput?.styleConfigTemplate?.name,
        product: this.facadeProductName
      } as IScratchTemplateDialogData
    });

    dialogRef.afterClosed().subscribe((scratchId: string | undefined) => {
      if (!scratchId) {
        return;
      }
      this.selectedStyleTemplateId = scratchId;
      this.render();
    });
  }

  private loadRenderers(): void {
    // queueName targets a specific renderer by name (see panorama loadHome). Match the old home-viewer
    // page: list the renderer names, minus the -vanity/-ifc worker variants that aren't picked directly.
    this.analyticsClient.getRendererNames(new GetRendererNamesRequest())
      .then(response => {
        this.renderers = (response.rendererNames ?? [])
          .filter(name => name.toLowerCase().indexOf('-vanity') === -1 && name.toLowerCase().indexOf('-ifc') === -1)
          .sort((a, b) => a.localeCompare(b));
      })
      .catch(() => {
        this.renderers = [];
      });
  }

  /* view data */
  renderedOutput: GetSituFacadeResponseExtended | undefined;
  isDaydreamPermitted: boolean = false;
  secondryFacade: GetSituFacadeResponseExtended | undefined;
  selectedFacadeFile: string | undefined;
  readonly renderedOutputStatusEnum = RenderedOutputStatus;
  refreshToken: string | undefined;
  saveTemplateTrigger: string | undefined;
  styleTemplateSavePending: boolean = false;
  @ViewChild('homeConfiguration') homeConfiguration: ElementRef | undefined;
  @ViewChild('styleSelections') styleSelections: ElementRef | undefined;
  @ViewChild('configurationOptions') configurationOptions: ElementRef | undefined;
  homeName: string | undefined;
  
  /* form set up */
  facadeSettingsForm!: FormGroup;
  get facadeConfigControl(): AbstractControl { return this.facadeSettingsForm?.controls?.facadeConfigName; }

  /* embed code */
  apiCode = '';
  prismApiCode = '';
  embedCode = '';
  prismEmbedCode = '';
  highlighted: boolean = false;
  embedFacade: boolean = true;

  private _loading = false;
  get loading(): boolean { return this._loading; }
  set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  static readonlyType: string = "facade";

  constructor(
    authService: AuthService,
    stateService: StateService,
    private clipboard: Clipboard,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private configService: ConfigService,
    private formBuilder: FormBuilder,
    private companyService: CompanyService,
    private highlightService: HighlightService,
    private route: ActivatedRoute,
    private router: Router,
    private renderServiceClient: SituRenderServiceWebApiClient,
    private situViewClient: SituViewWebApiClient,
    private dialogService: ConfirmDialogService,
    private homeService: HomeService,
    private facadeHomesService: FacadeHomesService,
    private facadeDownloadService: FacadeDownloadService,
    private analyticsClient: SituAnalyticsWebApiClient,
    private dialog: MatDialog) {
      super(authService, stateService);
      if (this.route.snapshot.params['id']) {
        this.paramId = this.route.snapshot.params['id'];
      } else if (this.route.snapshot.queryParams['id']) {
        this.paramId = this.route.snapshot.queryParams['id'];
      }
      this.breadcrumbService.setParam("home", "situFACADE");
  }

  protected onSelectedCompanyChange(): void {
    if (!this.initialised) {
      this.initFacadeSettingsForm()
        .then(() => {
          this.initialised = true;
        });
    }

    if (this.renderedOutput?.companyId && this.selectedCompany?.id && this.renderedOutput.companyId !== this.selectedCompany.id) {
      this.clearFacade();
      this.getDefault()
        .then((response: void | GetDefaultRenderedOutputResponse) => {
          if (response?.renderedOutputId) {
            this.loadById(response.renderedOutputId);
          }
        });
    }
  }

  protected onUserChange(): void {
    this.isReleasePermitted = this.authService.hasAnyRole([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin, Role.Release]);
    this.isDaydreamPermitted = this.authService.hasSituAccess() || this.authService.hasRole(Role.SituDaydream);
  }

  /** Take the facade being viewed, on the image shown, into situDAYDREAM. A Daydream reopens there with its own settings. */
  goToDaydream(): void {
    if (this.renderedOutput?.id) {
      const file = this.effectiveFacadeFile;
      this.router.navigate(['/situ-daydream', this.renderedOutput.id], file ? { queryParams: { file } } : {});
    }
  }

  ngAfterViewInit() {
    if (this.paramId) { //load by param first
      this.loadById(this.paramId, this.paramToId);
    } else { //then try load a default
      this.getDefault()
        .then((response: void | GetDefaultRenderedOutputResponse) => {
          if (response?.renderedOutputId) {
            this.loadById(response.renderedOutputId);
          }
        });
    }
  }
  
  ngAfterViewChecked() {
    if (!this.highlighted) {
      this.highlightService.highlightAll();
      this.highlighted = true;
    }
  }

  /* from children */
  selectedHomeId: string | undefined;
  selectedHomeConfigId: string | undefined;
  selectedStyleTemplateId: string | undefined;

  onHomeIdUpdates(homeId: string) {
    if (homeId && this.selectedHomeId !== homeId) {
      this.breadcrumbService.setParam("home", "situFACADE");
      this.selectedHomeId = homeId;
    }
  }

  onHomeConfigUpdates(homeConfig: HomeConfig) {
      //this.homeName = homeConfig.home?.toLowerCase();
      this.breadcrumbService.setParam("home", "situFACADE - " + homeConfig.home?.toLowerCase());
    }
  
  onHomeConfigIdUpdates(homeConfigId: string) {
    if (homeConfigId) {
      if (this.selectedHomeConfigId !== homeConfigId) {
        this.selectedHomeConfigId = homeConfigId;
      }
    } else {
      this.selectedHomeConfigId = homeConfigId;
    }
  }

  onStyleTemplateIdUpdates(styleTemplateId: string) {
    if (this.renderedOutput?.styleConfigTemplate && this.selectedStyleTemplateId === this.renderedOutput.styleConfigTemplate.id && (!styleTemplateId || styleTemplateId !== this.selectedStyleTemplateId)) {
      this.clearFacade();
    }
    if (styleTemplateId) {
      if (this.selectedStyleTemplateId !== styleTemplateId) {
        this.selectedStyleTemplateId = styleTemplateId;
      }
    } else {
      this.selectedStyleTemplateId = styleTemplateId;
    }
  }

  onSavePendingUpdates(savePending: boolean) {
    if (this.styleTemplateSavePending !== savePending) {
      this.styleTemplateSavePending = savePending;
    }
  }

  loadComparisonById(renderedOutputId: string) {
    this.getSituFacadeById(renderedOutputId)
      .then((getFacadeResponse: GetSituFacadeResponse) => {
        if (getFacadeResponse?.success) {
          let extendedResponse = new GetSituFacadeResponseExtended(getFacadeResponse);
          this.updateParams(this.renderedOutput?.id, getFacadeResponse.id);
          this.generateUrls(extendedResponse);
          this.secondryFacade = extendedResponse;
        }
      });
  }

  onIssueSelect(issueUrl: string) {
    let processedUrl = this.parseUrl(issueUrl);
    if (processedUrl?.artifactId) {
      this.debugData = processedUrl;
      this.loadById(processedUrl?.artifactId);
    }
  }
  debugData: { artifactId: string; queryParams: Record<string, string> } | undefined;
  parseUrl(url: string): { artifactId: string; queryParams: Record<string, string> } {
    const result: { artifactId: string; queryParams: Record<string, string> } = {
      artifactId: '',
      queryParams: {},
    };
  
    try {
      const pathAndQuery = url.split('/').slice(3).join('/');
      const [path, queryString] = pathAndQuery.split('?');
  
      const guidMatch = path.match(/^[0-9a-fA-F-]{36}$/);
      if (guidMatch) {
        result.artifactId = guidMatch[0];
      }
  
      if (queryString) {
        queryString.split('&').forEach((param) => {
          const [key, value] = param.split('=');
          if (key) {
            result.queryParams[key] = decodeURIComponent(value || '');
          }
        });
      }
    } catch (error) {
      console.error('Error parsing URL:', error);
    }
  
    return result;
  }
  /* from children */

  scrollToHomeConfiguration() {
    this.homeConfiguration!.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  scrollToStyleSelections() {
    this.styleSelections!.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  scrollToConfigurationOptions() {
    this.configurationOptions!.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  renderChild(renderConfig: string, existingArtifactId: string) {
    if (!renderConfig || !this.renderedOutput) {
      return;
    }

    let options = {
      title: `Render ${renderConfig}`,
      message: `Are you sure you want to render this facade at ${renderConfig}? Excessive high quality renders may cause delays.`,
      cancelText: 'Cancel',
      confirmText: 'Render',
      actionIcon: 'add',
      actionColour: 'primary'
    } as ConfirmDialogData;

    this.dialogService.open(options);
    this._subscriptions.push(this.dialogService.confirmed()
      .subscribe(confirmed => {
        if (confirmed) {
          if (existingArtifactId) {
            this.loadComparisonById(existingArtifactId);
          } else {
            this.createSituFacadeChildInternal(renderConfig)
            .then((response: GetSituFacadeResponse | void) => {
              if (response && response?.success && response?.id) {
                if (response.status === RenderedOutputStatus.Complete) {
                  let extendedResponse = new GetSituFacadeResponseExtended(response);
                  this.generateUrls(extendedResponse);
                  this.generateEmbedCode(extendedResponse);
                  this.generateResArtifactIds(extendedResponse);
                  this.renderedOutput = extendedResponse;
                  this.updateParams(extendedResponse?.id);
                  this.loadWithIdsForComparison();
                } else {
                  this.startPollingRenderStatus(response.id);
                }
              }
            });
          }
        }
      })
    );
  }

  createSituFacadeChildInternal(renderConfig: string): Promise<void | GetSituFacadeResponse> {
    let request = new CreateSituFacadeChildRequest();
    request.companyId = this.selectedCompany?.id;
    request.renderedOutputId = this.renderedOutput?.id;
    request.configurationName = renderConfig;

    return this.situViewClient.createSituFacadeChild(request)
      .then((response: GetSituFacadeResponse) => {
          if (!response.success) {
            this.showMessage(`Sorry! There was an issue rendering this facade at ${renderConfig}. Please try again.`);
          } else {
            this.showMessage(`You have successfully queued this facade to render at ${renderConfig}`);
          }
          return response;
      })
      .catch((x: any) => {
          this.showMessage("Sorry! There was an issue rendering this facade at a higher resolution. Please try again.");
          return 
      });
  }

  setDefault(isGlobal: boolean = false) {
    let request = new SetDefaultRenderedOutputRequest();
    request.companyId = this.selectedCompany?.id;
    request.productName = this.facadeProductName;
    request.isUserRequest = !isGlobal;
    request.renderedOutputId = this.renderedOutput?.id;

    this.situViewClient.setDefaultRenderedOutput(request)
      .then((response: SetDefaultRenderedOutputResponse) => {
          if (!response.success) {
              this.showMessage("Sorry! There was an issue setting the default. Please try again.");
          } else {
              this.showMessage(`You have successfully set this configuration as ${isGlobal ? 'the global' : 'your'} default!`);
          }
      })
      .catch((x: any) => {
          this.showMessage("Sorry! There was an issue at our end setting the default. Please try again.");
      });
  }

  getDefault(): Promise<void | GetDefaultRenderedOutputResponse> {
    let request = new GetDefaultRenderedOutputRequest();
    request.companyId = this.selectedCompany?.id;
    request.productName = this.facadeProductName;
    request.isUserRequest = true;

    return this.situViewClient.getDefaultRenderedOutput(request)
        .then((response: GetDefaultRenderedOutputResponse) => {
            return response;
        })
        .catch((x: any) => {
            this.showMessage("Sorry! There was an issue at our end getting the default. Please try again.");
        });
  }

  toggleCollapse() {
    this.collapsed = !this.collapsed;
  };

  clearList(configType: string) {
    switch (configType) {
      case 'facade':
        this.facadeConfigControl.reset();
        this.facadeConfigControl.updateValueAndValidity();
        this.facadeConfigControl.markAsTouched();
        break;
    }
  }

  copyToClipboard(text: string) {
    this.clipboard.copy(text);
    this.snackBar.open(`Copied to clipboard.`, "Close", {
      duration: 5000,
      panelClass: ['copied-snackbar']
    });
  }

  clearFacade() {
    this.loading = false;
    this.renderedOutput = undefined;
    this.secondryFacade = undefined;
    this.stopPollingRenderStatus();
  }

  saveTemplate() {
    this.saveTemplateTrigger = Guid.create().toString();
  }

  updateParams(renderedOutputId?: string, comparisonRenderedOutputId?: string) {
    const urlTree = this.router.createUrlTree([], {
      queryParams: { id: renderedOutputId },
      queryParamsHandling: "merge",
      preserveFragment: true
    });

    if (renderedOutputId) {
      if (comparisonRenderedOutputId) {
        urlTree.queryParams = comparisonRenderedOutputId ? { id: renderedOutputId, to: comparisonRenderedOutputId } : {};
      } else {
        urlTree.queryParams = renderedOutputId ? { id: renderedOutputId } : {};
      }
    }

    this.router.navigateByUrl(urlTree);
  }

  queue() {
    if (this.renderedOutput) {
      let request = new AddPriorityConfigRequest();
      request.companyId = this.selectedCompany?.id;
      request.productName = ProductName.Facade;
      let priorityConfig = new PriorityConfig();
      request.priorityConfig = priorityConfig;

      this.renderServiceClient.addPriorityConfig(request)
        .then((response: AddPriorityConfigResponse) => {
          if (response.success) {
            this.showMessage("Success! This home config has been added to your priority home queue.");
          } else {
            this.showMessage("Sorry! There was an issue at our end adding this home config to the priority render queue. Please try again.");
          }
        })
        .catch((err: any) => { this.showMessage("Sorry! There was an issue at our end adding this home config to the priority render queue. Please try again.")});
    }
  };

  download(artifactId: string) {
    if (artifactId !== this.renderedOutput?.artifactId) {
      this.loadComparisonById(artifactId);
    }

    this.facadeDownloadService.download(this.selectedCompany?.id, [artifactId])
      .catch((err: any) => this.showMessage("There was an error downloading the file!"));
  }

  browse(styleId?: string, configId?: string) {
    let queryParams;
    if (styleId && configId) {
      queryParams = { queryParams: { style: styleId, config: configId } };
    } else if (styleId) {
      queryParams = { queryParams: { style: styleId } };
    } else if (configId) {
      queryParams = { queryParams: { config: configId } };
    } else {
      queryParams = {};
    }
    this.router.navigate(['/situ-facade/browse', this.facadeProductName], queryParams);
  }

  render() {
    this.loading = true;
    this.renderedOutput = undefined;
    this.secondryFacade = undefined;
    this.stopPollingRenderStatus();

    let request = new CreateSituFacadeRequest();
    request.companyId = this.selectedCompany?.id;
    request.homeConfigId = this.selectedHomeConfigId;
    request.styleConfigTemplateId = this.selectedStyleTemplateId;
    request.configurationName = this.facadeSettingsForm.get("facadeConfigName")?.value

    // Advanced mode: override the renderer (queue) and/or environment when explicitly selected
    if (this.advancedMode) {
      if (this.selectedRenderer) {
        request.queueName = this.selectedRenderer;
      }
      if (this.selectedEnvironment !== undefined && this.selectedEnvironment !== null) {
        let environment = new ArtifactEnvironment();
        environment.company = this.selectedCompany?.code;
        environment.environment = this.selectedEnvironment;
        request.environmentOverride = environment;
      }
    }

    if (!request.homeConfigId || !request.styleConfigTemplateId || !request.configurationName) {
      this.loading = false;
      this.showMessage("Sorry! The config does not appear to be valid. Please check your config and try again.");
      return;
    }

    this.situViewClient.createSituFacade(request)
      .then((createFacadeResponse: GetSituFacadeResponse) => {
        if (createFacadeResponse.success && createFacadeResponse?.id) {
          if (createFacadeResponse.status === RenderedOutputStatus.Complete) {
            let extendedResponse = new GetSituFacadeResponseExtended(createFacadeResponse);
            this.generateUrls(extendedResponse);
            this.generateEmbedCode(extendedResponse);
            this.generateResArtifactIds(extendedResponse);
            this.renderedOutput = extendedResponse;
            this.updateParams(extendedResponse?.id);
            this.loadWithIdsForComparison();
          } else {
            this.startPollingRenderStatus(createFacadeResponse.id);
          }
        } else {
          this.showMessage("Sorry! There was an issue at our end retrieving your renders. Please try again.");
        }
      })
      .catch((x: any) => {
        this.showMessage("Sorry! There was an issue at our end retrieving your renders. Please try again.");
      });
  }

  loadById(renderedOutputId: string, comparisonRenderedOutputId: string | undefined = undefined, isPolling: boolean = false) {
    this.loading = true;

    if (!isPolling) {
      this.paramId = undefined;
      this.renderedOutput = undefined;
      this.breadcrumbService.setParam("home", "situFACADE");
      this.secondryFacade = undefined;
    }

    this.getSituFacadeById(renderedOutputId)
      .then((getFacadeResponse: GetSituFacadeResponse) => {
        /* do the company check */
        if (getFacadeResponse?.companyId != this.selectedCompany?.id && getFacadeResponse?.companyId) {
          if (!this.authService.isAuthorizedForCompanyById(getFacadeResponse.companyId)) {
            this.showMessage(`Sorry! Your are not authorized for company ${getFacadeResponse}.`);
            this.router.navigateByUrl('/unauthorized');
            return;
          }

          this.companyService.getCompanyById(getFacadeResponse.companyId)
            .then((company) => {
              if (!company) return;

              this.stateService.state = {
                ...this.stateService.state,
                company
              };
            });
        }

        if (getFacadeResponse?.success && getFacadeResponse?.id) {
          if (this.renderedOutput?.id !== getFacadeResponse?.id || this.renderedOutput.status !== getFacadeResponse?.status) {
            let extendedResponse = new GetSituFacadeResponseExtended(getFacadeResponse);
            if (this.renderedOutput?.id !== getFacadeResponse?.id) {
              this.generateUrls(extendedResponse);
              this.generateEmbedCode(extendedResponse);
              this.generateResArtifactIds(extendedResponse);
              
              // Store the response but don't set renderedOutput yet to prevent race condition
              const tempRenderedOutput = extendedResponse;
              tempRenderedOutput.status = extendedResponse.status;

              this.updateParams(tempRenderedOutput?.id!);
              if (tempRenderedOutput.facadeConfigName) {
                this.facadeSettingsForm.get('facadeConfigName')?.patchValue(getFacadeResponse.facadeConfigName);
              }

              // Wait a tick to ensure home config loading starts before setting renderedOutput
              setTimeout(() => {
                this.renderedOutput = tempRenderedOutput;
                if (this.renderedOutput?.status && [RenderedOutputStatus.Complete].includes(this.renderedOutput.status)) {
                  this.getReleaseItems();
                }
              }, 100);

            } else if (this.renderedOutput.status !== getFacadeResponse?.status) {
              this.renderedOutput.status = getFacadeResponse.status;
            }

            if (!this.renderedOutput?.status || !this.renderedOutput?.id) return;

            if (!isPolling && ([RenderedOutputStatus.InProgress, RenderedOutputStatus.Queued].includes(this.renderedOutput?.status))) {
              this.startPollingRenderStatus(this.renderedOutput.id!);
              return;
            }

            if ([RenderedOutputStatus.Failed].includes(this.renderedOutput.status)) {
              this.loading = false;
              this.stopPollingRenderStatus();
              this.showMessage("Sorry! Our system failed trying to render your facade. Please try again.");
              return;
            }
            
            if ([RenderedOutputStatus.Complete].includes(this.renderedOutput?.status)) {
              this.loading = false;
              if (this.pollingInterval) {
                this.homeService.clearRenderedOutputsCache(this.selectedCompany?.id, getFacadeResponse?.productName);
              }
              this.stopPollingRenderStatus();
              this.getReleaseItems();
              if (this.renderedOutput?.status === RenderedOutputStatus.Complete) {
                if (comparisonRenderedOutputId) {
                  this.loadComparisonById(comparisonRenderedOutputId);
                } else {
                  this.loadWithIdsForComparison();
                }
              }
            }
          }
        } else {
          this.showMessage("Sorry! There was an issue at our end retrieving your render. Please try again.");
        }
      })
      .catch((err: any) => {
        this.showMessage("Sorry! There was an issue at our end retrieving your render. Please try again.");
      });
  }

  sendToLive() {
    let options = {
      title: `Send to Live?`,
      message: `Are you sure you want to send to Live? This might override Live renders on your website!`,
      cancelText: 'Cancel',
      confirmText: 'Confirm',
      actionIcon: 'add',
      actionColour: 'primary'
    } as ConfirmDialogData;

    this.dialogService.open(options);
    this._subscriptions.push(this.dialogService.confirmed()
      .subscribe(confirmed => {
        if (confirmed) {
          let request = new SetRenderedOutputProductionRequest();
          request.companyId = this.selectedCompany?.id;
          request.renderedOutputId = this.renderedOutput?.id;

          this.situViewClient.setRenderedOutputProduction(request)
            .then((response: SetRenderedOutputProductionResponse) => {
              if (response.success) {
                this.showMessage("This has been set to Live!");
                this.loadWithIdsForComparison();              
              }
            })
            .catch((x: any) => {
              this.showMessage("Sorry! There was an issue at our end. Please try again.");
            });
        }
      })
    );
  }

  private generateUrls(getFacadeResponse: GetSituFacadeResponseExtended): void {
    getFacadeResponse.imageUrl = this.getImageUrl(getFacadeResponse.artifactId!);
    //getFacadeResponse.situViewUrl = this.getSituViewUrl(getFacadeResponse.homeConfig!);
  }
  
  private generateEmbedCode(getFacadeResponse: GetSituFacadeResponseExtended): void {
    getFacadeResponse.apiCode = this.getApiCode(getFacadeResponse);
    getFacadeResponse.prismApiCode = Prism.highlight(getFacadeResponse.apiCode, Prism.languages['markup'], 'markup');
    getFacadeResponse.embedCode = this.getEmbedCode(getFacadeResponse);
    getFacadeResponse.prismEmbedCode = Prism.highlight(getFacadeResponse.embedCode, Prism.languages['markup'], 'markup');
  }

  private generateResArtifactIds(getFacadeResponse: GetSituFacadeResponseExtended): void {
    if (isDaydreamProduct(getFacadeResponse.productName)) {
      // A Daydream has a parent but is not a higher-resolution variant of it.
      getFacadeResponse.res2kArtifactId = getFacadeResponse.artifactId;
      return;
    }
    if (getFacadeResponse.parentRenderedOutputId) {
      getFacadeResponse.res2kArtifactId = getFacadeResponse.parentRenderedOutput?.artifactId;
      switch (getFacadeResponse.facadeConfigName) {
        case '4096x3072':
          getFacadeResponse.res4kArtifactId = getFacadeResponse.artifactId;
          break;
        case '8192x6144':
          getFacadeResponse.res8kArtifactId = getFacadeResponse.artifactId;
          break;
        case '16384x12288':
          getFacadeResponse.res16kArtifactId = getFacadeResponse.artifactId;
          break;
        default: //2048x1536
          break;
      }
    } else {
      getFacadeResponse.res2kArtifactId = getFacadeResponse.artifactId;
      if (getFacadeResponse.renderedOutputChildren) {
        getFacadeResponse.renderedOutputChildren.forEach(child => {
          if (child.callerReferenceKey?.endsWith('4096x3072') && child.status === RenderedOutputStatus.Complete) getFacadeResponse.res4kArtifactId = child.artifactId;
          if (child.callerReferenceKey?.endsWith('8192x6144') && child.status === RenderedOutputStatus.Complete) getFacadeResponse.res8kArtifactId = child.artifactId;
          if (child.callerReferenceKey?.endsWith('16384x12288') && child.status === RenderedOutputStatus.Complete) getFacadeResponse.res16kArtifactId = child.artifactId;
        });
      }
    }
  }

  private getSituFacadeById(renderedOutputId: string): Promise<GetSituFacadeResponse> {
    let request = new GetSituFacadeRequest();
    request.renderedOutputId = renderedOutputId;

    return this.situViewClient.getSituFacade(request);
  }

  private loadWithIdsForComparison() {
    let request = new GetSituFacadeWithIdsRequest();
    request.homeConfigId = this.renderedOutput?.homeConfigId;
    request.styleConfigName = this.renderedOutput?.styleConfigTemplate?.name;
    this.situViewClient.getSituFacadeWithIds(request)
      .then((getSituFacadeResponse: GetSituFacadeResponse) => {
        if (getSituFacadeResponse.success) {
          let getSituFacadeResponseExtended = new GetSituFacadeResponseExtended(getSituFacadeResponse);
          this.generateUrls(getSituFacadeResponseExtended);
          this.secondryFacade = getSituFacadeResponseExtended;
        }
      })
      .catch((err: any) => { this.showMessage("Sorry! There was an issue at our end retrieving this render. Please try again.")});
  }

  /* polling */
  pollingInterval: NodeJS.Timeout | null = null;
  pollingFails = 0;

  private startPollingRenderStatus(renderedOutputId: string) {
    if (!this.pollingInterval) {
      this.pollingFails = 0;

      this.loadById(renderedOutputId, undefined, false); // start immediately in case it's a cached render
      this.pollingInterval = setInterval(() => { this.loadById(renderedOutputId, undefined, true); }, 10000);
    }
  }

  private stopPollingRenderStatus() {
    if (!!this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }
  /* polling */

  private initFacadeSettingsForm(): Promise<void> {
    const controlsConfigFacade: any = {
      facadeConfigName: ['default', Validators.required]
    };

    this.facadeSettingsForm = this.formBuilder.group(controlsConfigFacade);

    return new Promise(resolve => resolve());
  }

  private getApiCode(getFacadeResponse: GetSituFacadeResponseExtended) {
    let homeConfig = getFacadeResponse.homeConfig;
    const file = this.getEffectiveFileFor(getFacadeResponse ?? this.renderedOutput);
    const fileLine = file ? `\n            file: '${file}', //optional - the artifact image to display` : '';
    let apiCode =
  `<!-- 1. The <iframe> (and facade image) will replace this <div> tag. -->
  <div id="facade"></div>

  <script>
  // 2. This code loads the IFrame API code asynchronously.
    var tag = document.createElement('script');

    tag.src = "${this.configService.config.facadeEmbedBaseUrl}/api.js";
    var firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

  // 3. This function creates an <iframe> (and facade object) after
  // the API code downloads.
    var facade;
    function onSituIframeAPIReady() {
        facade = new Situ.Facade('facade', {
            homeConfig: {
              company: '${this.selectedCompany?.code}',
              home: '${homeConfig?.home}',
              masterFile: '${homeConfig?.masterFile}',
              options: ${JSON.stringify(homeConfig?.options || [])}
            },
            styleConfigName: '${getFacadeResponse.styleConfigTemplate?.name}',
            facadeConfigName: '${getFacadeResponse.facadeConfigName}', //optional${fileLine}
            //thumbWidth: '300', //in pixels - optional
            //productCalloutOptions: { //optional
            //  initialState: Situ.ProductCalloutState.Show, //Enum values - Show, Hide
            //  name: "Custom Products"
            //},
            events: {
                'onStateChange': onFacadeStateChange,
                'onError': onFacadeError
            }
        });
    }

  // 4. The API calls this function when the facade's state changes.
    function onFacadeStateChange(event) {
        switch (event.detail) {
            case Situ.FacadeState.Rendering:
                console.log("Facade is rendering");
                break;
            case Situ.FacadeState.Ready:
                console.log("Facade is ready");
                break;
        }
    }

  // 5. The API calls this function when the facade has an error.
    function onFacadeError(event) {
        console.log("Facade error: " + event.data);
    }
  // 6. Additonal functions available for the facade
    //facade.toggleProducts(); 
    //can be used to toggle products on and off external to the embed UI
  </script>`;

    return apiCode;
  }

  private getEmbedCode(getFacadeResponse: GetSituFacadeResponseExtended) {
    let embedCode = `<iframe title="Facade" src="${this.getLiveFacadeEmbedUrl(getFacadeResponse)}" style="width:100%;height:100%;" frameborder="0"></iframe>
                     <!-- add query param thumbWidth (in pixels) to src url to set the thumbnail width e.g. &thumbWidth=300 -->`;
    return embedCode;
  }

  private getImageUrl(artifactId: string, fileName: string = 'facade.jpg') {
    return `${this.configService.config.situViewWebApiBaseUrl}/home/GetImage?artifactId=${artifactId}&fileName=${encodeURIComponent(fileName)}`;
  }

  get currentTopViewerImageUrl(): string | undefined {
    if (!this.renderedOutput?.artifactId) return undefined;
    const file = this.effectiveFacadeFile;
    if (!file) return undefined;
    return this.getImageUrl(this.renderedOutput.artifactId, file);
  }

  share(forInternal: boolean = false) {
    const secondryFacadeId = this.secondryFacade?.id ? forInternal ? `&to=${this.secondryFacade.id}` : `${this.secondryFacade.id}` : "";
    if (this.renderedOutput?.id) {
      if (forInternal) {
        this.clipboard.copy(`${this.configService.config.situPortalBaseUrl}/situ-facade?id=${this.renderedOutput.id}${secondryFacadeId}`);
        this.showMessage("Copied url to clipboard!");
      } else {
        if (secondryFacadeId) {
          let url = this.configService.config.situLinkBaseUrl ?
          `https://${SituFacadeComponent.readonlyType}.${this.configService.config.situLinkBaseUrl}/${secondryFacadeId}/${this.renderedOutput.id}` :
          `${this.configService.config.situPortalBaseUrl}/${secondryFacadeId}/${this.renderedOutput.id}`;
          this.clipboard.copy(url);
          this.showMessage("Copied situLINK to clipboard!");
        } else {
          let url = this.configService.config.situLinkBaseUrl ?
          `https://${SituFacadeComponent.readonlyType}.${this.configService.config.situLinkBaseUrl}/${this.renderedOutput.id}` :
          `${this.configService.config.situPortalBaseUrl}/${this.renderedOutput.id}`;
          this.clipboard.copy(url);
          this.showMessage("Copied situLINK to clipboard!");
        }
      }
    };
  };

  private getLiveFacadeEmbedUrl(getFacadeResponse?: GetSituFacadeResponseExtended, forIframe: boolean = false) {
    let url: string;
    if (forIframe) {
      url = `${this.configService.config.facadeEmbedBaseUrl}/embed.html?id=${this.renderedOutput?.id}`;
    } else if (getFacadeResponse) {
      url = `${this.configService.config.facadeEmbedBaseUrl}/embed.html?hid=${getFacadeResponse.homeConfigId}&sid=${getFacadeResponse.styleConfigTemplate?.name}&fid=${getFacadeResponse.facadeConfigName}`;
    } else {
      url = `${this.configService.config.facadeEmbedBaseUrl}/embed.html?hid=${this.renderedOutput?.homeConfigId}&sid=${this.renderedOutput?.styleConfigTemplate?.name}&fid=${this.renderedOutput?.facadeConfigName}`;
    }
    const file = this.getEffectiveFileFor(getFacadeResponse ?? this.renderedOutput);
    if (file) {
      url += `&file=${encodeURIComponent(file)}`;
    }
    return url;
  }

  private getEffectiveFileFor(source?: { files?: HomeConfigFile[] }): string | undefined {
    const files = (source?.files ?? [])
      .map((f: HomeConfigFile) => f.fileName)
      .filter((name): name is string => !!name && /\.(jpe?g|png|webp)$/i.test(name));
    if (this.selectedFacadeFile && files.includes(this.selectedFacadeFile)) {
      return this.selectedFacadeFile;
    }
    return files[0];
  }

  get facadeImageFiles(): string[] {
    return (this.renderedOutput?.files ?? [])
      .map((f: HomeConfigFile) => f.fileName)
      .filter((name): name is string => !!name && /\.(jpe?g|png|webp)$/i.test(name));
  }

  get hasMultipleFacadeFiles(): boolean {
    return this.facadeImageFiles.length > 1;
  }

  get effectiveFacadeFile(): string | undefined {
    return this.getEffectiveFileFor(this.renderedOutput);
  }

  prevFacadeFile() {
    const files = this.facadeImageFiles;
    if (files.length <= 1) return;
    const current = this.effectiveFacadeFile ?? files[0];
    const i = files.indexOf(current);
    this.selectedFacadeFile = files[(i <= 0 ? files.length : i) - 1];
    this.regenerateEmbedCode();
  }

  nextFacadeFile() {
    const files = this.facadeImageFiles;
    if (files.length <= 1) return;
    const current = this.effectiveFacadeFile ?? files[0];
    const i = files.indexOf(current);
    this.selectedFacadeFile = files[(i + 1) % files.length];
    this.regenerateEmbedCode();
  }

  private regenerateEmbedCode() {
    if (this.renderedOutput) {
      this.generateEmbedCode(this.renderedOutput);
    }
  }

  releaseIssueDialogRef: MatDialogRef<ReleaseIssueDialogComponent> | undefined;
  addEditReleaseIssue() {
    let url = this.configService.config.situLinkBaseUrl ?
          `https://${SituFacadeComponent.readonlyType}.${this.configService.config.situLinkBaseUrl}/${this.renderedOutput!.id}` :
          `${this.configService.config.situPortalBaseUrl}/${this.renderedOutput!.id}`;
          
    this.releaseIssueDialogRef = this.dialog.open(ReleaseIssueDialogComponent, {
      panelClass: 'modal-panel',
      width: '60%',
      height: '80%',
      disableClose: true,
      data: {
        isSituUser: this.isSituUser,
        companyId: this.selectedCompany?.id,
        reportedSituLink: url,
        renderedOutputId: this.renderedOutput?.parentRenderedOutputId ? this.renderedOutput?.parentRenderedOutputId : this.renderedOutput?.id,
        linkedReleaseItemIds: this.releaseItems,
        reportedSituLinkProductName: ProductName.Facade
      }
    });

    this._subscriptions.push(this.releaseIssueDialogRef.afterClosed()
      .subscribe((data: ReleaseIssueDialogComponentResultData) => {
        if (data?.refreshNeeded) {
          this.showMessage("Release Issue has been logged!");
        }
      })
    );
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }

  /* releases */
  releaseItemDialogRef: MatDialogRef<ReleaseStyleTemplateDialogComponent> | undefined;
  copyToStyleTemplate(homeConfigIds: string[]) {
    if (!homeConfigIds || homeConfigIds.length < 1) {
      this.showMessage("Please select some home configurations");
      return;
    }

    this.releaseItemDialogRef = this.dialog.open(ReleaseStyleTemplateDialogComponent, {
      panelClass: 'modal-panel',
      width: '60%',
      height: '80%',
      data: {
        companyId: this.selectedCompany?.id,
        productName: this.facadeProductName,
        homeConfigIds: homeConfigIds
      }
    });

    this._subscriptions.push(this.releaseItemDialogRef.afterClosed()
      .subscribe((data: ReleaseStyleTemplateDialogComponentResultData) => {
        if (data) {
          const skipped = data.skippedCount ? ` (${data.skippedCount} already in release)` : '';
          this.showMessage(`${data.releaseItemCount} release items added across ${data.releaseCount} releases${skipped}!`);
        }
      })
    );
  }

  addToRelease(type: string) {
    if (!this.selectedHomeId || !this.selectedHomeConfigId || !this.selectedStyleTemplateId) {
      return;
    }
    switch (type) {
      case "Single":
        this.copyToStyleTemplate([this.selectedHomeConfigId!]);
        break;
      case "MasterFiles":
        let masterHomeConfigIds: string[] = [];
        this.getHomeConfig(this.selectedHomeConfigId)
          .then((getHomeConfigResponse: GetHomeConfigResponse | void) => {
            if (getHomeConfigResponse?.success && getHomeConfigResponse.homeConfig) {
              this.loadIfcFiles(this.selectedHomeId!)
                .then((response: any) => {
                  if (response.success && response.ifcFiles) {
                    let hcs = new HomeConfigSlim();
                    hcs.home = getHomeConfigResponse.homeConfig!.home;
                    hcs.options = getHomeConfigResponse.homeConfig!.options;
                    hcs.companyId = this.selectedCompany?.id;

                    const masterPromises: Promise<string | undefined>[] = response.ifcFiles
                      .filter((x: HomeIfcFile) => x.type === HomeIfcFileType.Master)
                      .map((ifcFile: HomeIfcFile) => {
                        hcs.masterFile = ifcFile.name;
                        return this.getHomeConfigId(hcs);
                      });

                    Promise.all(masterPromises)
                      .then((results) => {
                        results.forEach((homeConfigId) => {
                          if (homeConfigId) {
                            masterHomeConfigIds.push(homeConfigId);
                          }
                        });
                        this.copyToStyleTemplate(masterHomeConfigIds);
                      })
                      .catch((err) => {
                        this.showMessage("Failed to get home configs.");
                      });
                  } else {
                    this.showMessage("Failed to load ifc files.");
                  }
                })
                .catch((err: any) => {
                  this.showMessage("Failed to load ifc files.");
                })
                .finally(() => {
                  this.loading = false;
                });
            }
          });
        break;
      case "Options":
        let optionsHomeConfigIds: string[] = [];
        this.getHomeConfig(this.selectedHomeConfigId)
          .then((getHomeConfigResponse: GetHomeConfigResponse | void) => {
            if (getHomeConfigResponse?.success && getHomeConfigResponse.homeConfig) {
              let hcs = new HomeConfigSlim();
              hcs.home = getHomeConfigResponse.homeConfig!.home;
              hcs.masterFile = getHomeConfigResponse.homeConfig!.masterFile;
              hcs.companyId = this.selectedCompany?.id;

              if (!getHomeConfigResponse.homeConfig?.options || getHomeConfigResponse.homeConfig.options.length < 1) {
                return;
              }

              const optionsPromises: Promise<string | undefined>[] = getHomeConfigResponse.homeConfig.options
                .map((option: string) => {
                  hcs.options = [option];
                  return this.getHomeConfigId(hcs);
                });

              Promise.all(optionsPromises)
                .then((results) => {
                  results.forEach((homeConfigId) => {
                    if (homeConfigId) {
                      optionsHomeConfigIds.push(homeConfigId);
                    }
                  });
                  this.copyToStyleTemplate(optionsHomeConfigIds);
                })
                .catch((err) => {
                  this.showMessage("Failed to get home configs.");
                });
            }
          });
        break;
    }
  }

  /* add the current master file (facade) to release for every home that has it */
  findingFacadeHomes: boolean = false;
  async addFacadeToAllHomes() {
    const companyId = this.selectedCompany?.id;
    if (!companyId || !this.selectedHomeConfigId || this.findingFacadeHomes) {
      return;
    }

    this.findingFacadeHomes = true;
    try {
      const result = await this.facadeHomesService.selectHomeConfigIdsForFacade(companyId, this.selectedHomeConfigId, true);
      if (!result) {
        return;
      }
      if (result.failedCount > 0) {
        this.showMessage(`Could not get a home configuration for ${result.failedCount} home(s); they were left out.`);
      }
      this.copyToStyleTemplate(result.homeConfigIds);
    } catch (err: any) {
      this.showMessage(err?.message || "Failed to find homes for this facade.");
    } finally {
      this.findingFacadeHomes = false;
    }
  }

  private loadIfcFiles(homeId: string) {
    if (!this.selectedCompany?.code) {
      return Promise.resolve();
    }
    this.loading = true;

    var request = new ListIfcFilesByHomeIdRequest();
    request.companyId = this.selectedCompany.id;
    request.homeId = homeId;

    return this.homeService.ListIfcFilesByHomeId(request);
  }  
  
  private getHomeConfig(homeConfigId: string) {
    if (!this.selectedCompany?.code) {
      return Promise.resolve();
    }
    this.loading = true;

    let request = new GetHomeConfigRequest();
    request.homeConfigId = homeConfigId;

    return this.situViewClient.getHomeConfig(request);
  }

  private getHomeConfigId(homeConfig: HomeConfigSlim) {
    let request = new GetHomeConfigSlimIdRequest();
    request.companyId = this.selectedCompany?.id;
    request.homeConfig = homeConfig;

    return this.homeService.GetHomeConfigId(request)
      .then((response: GetHomeConfigSlimIdResponse) => {
        if (!response?.success) {
          this.showMessage("Home Config is invalid.");
        } else {
          return response.homeConfigId;
        }
      });
  }

  hasReleaseItems: boolean = false;
  releaseItems: string[] | undefined;
  private getReleaseItems() {
    if (!this.selectedCompany?.id || !this.renderedOutput?.id || !this.isReleasePermitted) {
      return;
    }

    let request = new ListReleaseItemsRequest();
    request.companyId = this.selectedCompany.id;
    request.renderedOutputId = this.renderedOutput.id;

    this.situViewClient.listReleaseItems(request)
      .then((response: ListReleaseItemsResponse) => {
        if (response?.success && response?.items && response?.items.length > 0) {
          this.hasReleaseItems = true;
          this.releaseItems = response.items.map(x => x.id).filter((id): id is string => id !== undefined);
        } else {
          this.hasReleaseItems = false;
          this.releaseItems = undefined;
        }
      })
  }
  /* releases */
}

export class GetSituFacadeResponseExtended extends GetSituFacadeResponse {
  imageUrl: string | undefined;
  situViewUrl: string | undefined;
  apiCode: string | undefined;
  prismApiCode: string | undefined;
  embedCode: string | undefined;
  prismEmbedCode: string | undefined;
  res2kArtifactId: string | undefined;
  res4kArtifactId: string | undefined;
  res8kArtifactId: string | undefined;
  res16kArtifactId: string | undefined;

  constructor(baseInstance: GetSituFacadeResponse) {
    super(baseInstance);
  }
}
